import { Text, Picture } from '@farmersdog/corgi-x';
import type {
  ReviewsCarouselItemProps,
  VideoReview,
  DoubleImageReview,
  SingleImageReview,
} from '../reviewsData';

import styles from './ReviewsCarouselItem.module.css';
import { ExtendedVideoSection } from 'src/pages/HomepageRedesign/components/ExtendedVideoSection';
import { useRef } from 'react';

function isVideoReview(props: ReviewsCarouselItemProps): props is VideoReview {
  return 'mobileWistiaId' in props && !!props.mobileWistiaId;
}

function isDoubleImageReview(
  props: ReviewsCarouselItemProps
): props is DoubleImageReview {
  return 'doubleImage' in props && props.doubleImage !== undefined;
}

function isSingleImageReview(
  props: ReviewsCarouselItemProps
): props is SingleImageReview {
  return 'image' in props && props.image !== undefined;
}

export type ReviewsCarouselItemExtendedProps = ReviewsCarouselItemProps & {
  isActive?: boolean;
  isMobile?: boolean;
};

const renderImageContent = (props: ReviewsCarouselItemExtendedProps) => {
  if (isDoubleImageReview(props)) {
    return (
      <div className={styles.doubleImageWrapper}>
        <div className={styles.imageBlock}>
          <Picture
            alt={props.alts[0]}
            sources={props.doubleImage[0]}
            className={styles.image}
            loading="lazy"
          />
          {props.showBeforeAfterCopy && (
            <Text
              as="p"
              bold
              topSpacing="xs"
              bottomSpacing="none"
              variant="heading-12"
              className={styles.label}
            >
              Before
            </Text>
          )}
        </div>
        <div className={styles.imageBlock}>
          <Picture
            alt={props.alts[1]}
            sources={props.doubleImage[1]}
            className={styles.image}
            loading="lazy"
          />
          {props.showBeforeAfterCopy && (
            <Text
              as="p"
              bold
              topSpacing="xs"
              bottomSpacing="none"
              variant="heading-12"
              className={styles.label}
            >
              After
            </Text>
          )}
        </div>
      </div>
    );
  }

  if (isSingleImageReview(props)) {
    return (
      <Picture
        alt={props.alt}
        sources={props.image}
        className={styles.singleImage}
        loading="lazy"
      />
    );
  }

  return null;
};

export const ReviewsCarouselItem = (
  props: ReviewsCarouselItemExtendedProps
) => {
  const hasMounted = useRef(false);
  if (isVideoReview(props)) {
    if (!hasMounted.current && props.isActive) {
      hasMounted.current = true;
    }
    return (
      <div className={styles.videoCard}>
        {props.isMobile ? (
          hasMounted.current ? (
            <ExtendedVideoSection
              videoTitle={props.title}
              videoTrackingTag={props.videoTrackingTag}
              moduleName="Reviews Carousel"
              wistiaId={props.mobileWistiaId}
              deferMountUntilScroll={false}
              className={styles.videoContainer}
              activeSlideInGallery={props.isActive}
              wistiaOptions={{
                autoPlay: false,
                playsinline: true,
                endVideoBehavior: 'reset',
                playPauseNotifier: false,
                videoFoam: true,
                playButton: false,
              }}
              loadingPlaceholder={true}
            />
          ) : (
            <div className={styles.card} />
          )
        ) : (
          <ExtendedVideoSection
            videoTitle={props.title}
            videoTrackingTag={props.videoTrackingTag}
            moduleName="Reviews Carousel"
            wistiaId={props.desktopWistiaId}
            deferMountUntilScroll={false}
            className={styles.videoContainer}
            activeSlideInGallery={true}
            wistiaOptions={{
              autoPlay: false,
              playsinline: true,
              endVideoBehavior: 'reset',
              playPauseNotifier: false,
              videoFoam: true,
              playButton: false,
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.card}>
      {renderImageContent(props)}
      <Text
        as="p"
        vSpacing="sm"
        color="kale-3"
        variant="article-16"
        className={styles.testimonial}
      >
        “{props.testimonial}”
      </Text>
      <Text variant="heading-12" weight="semibold" className={styles.author}>
        — {props.dogName + '’s person'}
      </Text>
    </div>
  );
};
